<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">

          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <h6 class="mb-0">Orden del catálogo</h6>
              <div class="d-flex gap-2">
                <router-link :to="{ name: 'Inventory' }" class="btn btn-outline-secondary btn-sm">
                  <i class="fa fa-arrow-left me-1"></i> Volver
                </router-link>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  @click="save"
                  :disabled="isSaving || !hasChanges"
                >
                  <i class="fa fa-save me-1"></i> {{ isSaving ? 'Guardando...' : 'Guardar orden' }}
                </button>
              </div>
            </div>
            <p class="text-muted text-sm mb-3">
              Arrastra los productos (o usa las flechas) para definir en qué orden salen en el catálogo.
              Solo aparecen los productos visibles en catálogo. Los que marques como visibles después salen al final.
            </p>
          </div>

          <div class="card-body pt-0">
            <div v-if="isLoading" class="text-center text-sm text-muted py-4">Cargando productos...</div>
            <div v-else-if="!items.length" class="text-center text-sm text-muted py-4">
              No hay productos visibles en el catálogo.
            </div>

            <ul v-else class="order-list">
              <li
                v-for="(item, index) in items"
                :key="item.id"
                class="order-item"
                :class="{ 'order-item--dragging': dragIndex === index, 'order-item--over': overIndex === index && dragIndex !== index }"
                draggable="true"
                @dragstart="onDragStart(index, $event)"
                @dragover.prevent="overIndex = index"
                @dragleave="overIndex = overIndex === index ? null : overIndex"
                @drop.prevent="onDrop(index)"
                @dragend="onDragEnd"
              >
                <i class="fa fa-grip-vertical order-grip"></i>
                <span class="order-pos">{{ index + 1 }}</span>
                <img v-if="item.image_url" :src="item.image_url" :alt="item.name" class="order-img" />
                <div v-else class="order-img order-img--ph"><i class="fa fa-tshirt"></i></div>
                <div class="order-info">
                  <h6 class="mb-0 text-sm">{{ item.name }}</h6>
                  <span v-if="item.ref" class="text-xs text-muted">Ref. {{ item.ref }}</span>
                  <span v-if="item.status != 1" class="badge badge-sm bg-gradient-warning ms-2">Inactivo</span>
                </div>
                <div class="order-actions">
                  <button type="button" class="btn btn-link btn-sm mb-0 px-2" title="Mover al principio" :disabled="index === 0" @click="move(index, 0)">
                    <i class="fa fa-angle-double-up"></i>
                  </button>
                  <button type="button" class="btn btn-link btn-sm mb-0 px-2" title="Subir" :disabled="index === 0" @click="move(index, index - 1)">
                    <i class="fa fa-arrow-up"></i>
                  </button>
                  <button type="button" class="btn btn-link btn-sm mb-0 px-2" title="Bajar" :disabled="index === items.length - 1" @click="move(index, index + 1)">
                    <i class="fa fa-arrow-down"></i>
                  </button>
                </div>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import inventoryService from '../../../services/inventory.service';

export default {
  name: 'CatalogOrder',
  data() {
    return {
      items: [],
      savedIds: [],
      isLoading: false,
      isSaving: false,
      dragIndex: null,
      overIndex: null,
    };
  },
  computed: {
    hasChanges() {
      return this.items.map((i) => i.id).join(',') !== this.savedIds.join(',');
    },
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const res = await inventoryService.getCatalogOrder();
        this.items = res.data || [];
        // Si algún producto aún no tiene posición, el orden mostrado es nuevo y se puede guardar
        this.savedIds = this.items.some((i) => i.catalog_order === null)
          ? []
          : this.items.map((i) => i.id);
      } catch (e) {
        console.error('Error al cargar el orden del catálogo:', e);
        Swal.fire('Error', 'No se pudieron cargar los productos del catálogo', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    move(from, to) {
      if (to < 0 || to >= this.items.length || from === to) return;
      const [item] = this.items.splice(from, 1);
      this.items.splice(to, 0, item);
    },
    onDragStart(index, event) {
      this.dragIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },
    onDrop(index) {
      if (this.dragIndex !== null) this.move(this.dragIndex, index);
      this.onDragEnd();
    },
    onDragEnd() {
      this.dragIndex = null;
      this.overIndex = null;
    },
    async save() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        const ids = this.items.map((i) => i.id);
        await inventoryService.saveCatalogOrder(ids);
        this.savedIds = ids;
        Swal.fire({ icon: 'success', title: '¡Listo!', text: 'Orden del catálogo guardado.', timer: 1500, showConfirmButton: false });
      } catch (e) {
        console.error('Error al guardar el orden del catálogo:', e);
        Swal.fire('Error', 'No se pudo guardar el orden del catálogo', 'error');
      } finally {
        this.isSaving = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.order-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.order-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 6px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  cursor: grab;
  user-select: none;
}
.order-item--dragging {
  opacity: 0.4;
}
.order-item--over {
  border-color: #5e72e4;
  box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.25);
}
.order-grip {
  color: #adb5bd;
}
.order-pos {
  min-width: 28px;
  font-weight: 600;
  font-size: 0.875rem;
  color: #5e72e4;
  text-align: center;
}
.order-img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
}
.order-img--ph {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f3f5;
  color: #adb5bd;
}
.order-info {
  flex: 1;
  min-width: 0;
}
.order-info h6 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.order-actions {
  display: flex;
  flex-shrink: 0;
}
</style>
